import React from 'react';
import './HomePage.css';

interface HomePageProps {
  onGetStarted: () => void;
  onViewDemo: () => void;
  onNavigate: (page: string) => void;
}

const HomePage: React.FC<HomePageProps> = ({ onGetStarted, onViewDemo, onNavigate }) => {
  return (
    <div className="homepage">
      {/* Header */}
      <header className="homepage__header">
        <div className="homepage__container">
          <div className="homepage__logo">
            <div className="homepage__logo-container">
              <div className="homepage__logo-icon">
                <div className="homepage__logo-cube">
                  <div className="homepage__logo-face homepage__logo-face--front"></div>
                  <div className="homepage__logo-face homepage__logo-face--back"></div>
                  <div className="homepage__logo-face homepage__logo-face--right"></div>
                  <div className="homepage__logo-face homepage__logo-face--left"></div>
                  <div className="homepage__logo-face homepage__logo-face--top"></div>
                  <div className="homepage__logo-face homepage__logo-face--bottom"></div>
                </div>
                <div className="homepage__logo-particles">
                  <div className="homepage__logo-particle homepage__logo-particle--1"></div>
                  <div className="homepage__logo-particle homepage__logo-particle--2"></div>
                  <div className="homepage__logo-particle homepage__logo-particle--3"></div>
                  <div className="homepage__logo-particle homepage__logo-particle--4"></div>
                </div>
              </div>
              <h1>Faathum</h1>
            </div>
            <span className="homepage__tagline">Advancing how data is understood</span>
          </div>
          <nav className="homepage__nav">
            <button className="homepage__nav-button" onClick={onGetStarted}>
              Get Started
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="homepage__hero">
        <div className="homepage__container">
          <div className="homepage__hero-content">
            <h2 className="homepage__hero-title">
              Advancing how data is understood
            </h2>
            <p className="homepage__hero-description">
              Faathum's Explore with AI platform enables organizations to create intelligent 
              data exploration widgets that connect users directly to leading AI platforms 
              with contextual, audience-specific prompts.
            </p>
            <div className="homepage__hero-actions">
              <button className="homepage__cta-primary" onClick={onGetStarted}>
                Create Your Widget
              </button>
              <button className="homepage__cta-secondary" onClick={onViewDemo}>
                View Demo
              </button>
            </div>
          </div>
          <div className="homepage__hero-visual">
            <div className="homepage__widget-preview">
              <div className="homepage__widget-header">
                <div className="homepage__widget-title">Explore with AI</div>
                <div className="homepage__widget-subtitle">Federal Reserve Data</div>
              </div>
              <div className="homepage__widget-content">
                <div className="homepage__widget-option">📊 General Analysis</div>
                <div className="homepage__widget-option">📈 Trend Analysis</div>
                <div className="homepage__widget-option">🔍 Anomaly Detection</div>
                <div className="homepage__widget-option">🏛️ Policy Analysis</div>
              </div>
              <div className="homepage__widget-platforms">
                <span className="homepage__platform">ChatGPT</span>
                <span className="homepage__platform">Claude</span>
                <span className="homepage__platform">Gemini</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="homepage__features">
        <div className="homepage__container">
          <h3 className="homepage__section-title">Why Choose Faathum?</h3>
          <div className="homepage__features-grid">
            <div className="homepage__feature">
              <div className="homepage__feature-icon">🎯</div>
              <h4>Audience-Targeted Prompts</h4>
              <p>Generate contextual prompts tailored for journalists, researchers, policymakers, or general audiences.</p>
            </div>
            <div className="homepage__feature">
              <div className="homepage__feature-icon">🔗</div>
              <h4>Multi-Platform Integration</h4>
              <p>Connect seamlessly with ChatGPT, Claude, Gemini, Perplexity, and Grok with pre-filled prompts.</p>
            </div>
            <div className="homepage__feature">
              <div className="homepage__feature-icon">⚡</div>
              <h4>Instant Deployment</h4>
              <p>Generate embed codes and deploy AI exploration widgets to your website in minutes.</p>
            </div>
            <div className="homepage__feature">
              <div className="homepage__feature-icon">📊</div>
              <h4>Smart Analysis Modes</h4>
              <p>Offer multiple analysis approaches: general insights, trend analysis, anomaly detection, and policy implications.</p>
            </div>
            <div className="homepage__feature">
              <div className="homepage__feature-icon">🛡️</div>
              <h4>Professional Disclaimers</h4>
              <p>Built-in warnings about third-party sites and AI limitations ensure responsible usage.</p>
            </div>
            <div className="homepage__feature">
              <div className="homepage__feature-icon">📱</div>
              <h4>Responsive Design</h4>
              <p>Widgets work perfectly across desktop, tablet, and mobile devices.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="homepage__use-cases">
        <div className="homepage__container">
          <h3 className="homepage__section-title">Perfect For</h3>
          <div className="homepage__use-cases-grid">
            <div className="homepage__use-case">
              <h4>🏛️ Government Agencies</h4>
              <p>Enable citizens and researchers to explore public datasets with AI-powered analysis tools.</p>
            </div>
            <div className="homepage__use-case">
              <h4>📰 News Organizations</h4>
              <p>Provide journalists and readers with intelligent data exploration capabilities for investigative reporting.</p>
            </div>
            <div className="homepage__use-case">
              <h4>🎓 Research Institutions</h4>
              <p>Offer students and researchers AI-assisted analysis of academic datasets and publications.</p>
            </div>
            <div className="homepage__use-case">
              <h4>🏢 Enterprises</h4>
              <p>Democratize data analysis across teams with user-friendly AI exploration interfaces.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="homepage__cta-section">
        <div className="homepage__container">
          <div className="homepage__cta-content">
            <h3>Ready to Transform Your Data Experience?</h3>
            <p>Join organizations worldwide using Faathum to make data more accessible and actionable.</p>
            <button className="homepage__cta-primary" onClick={onGetStarted}>
              Start Building Your Widget
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="homepage__footer">
        <div className="homepage__container">
          <div className="homepage__footer-content">
            <div className="homepage__footer-brand">
              <div className="homepage__footer-logo">
                <div className="homepage__logo-icon homepage__logo-icon--small">
                  <div className="homepage__logo-cube">
                    <div className="homepage__logo-face homepage__logo-face--front"></div>
                    <div className="homepage__logo-face homepage__logo-face--back"></div>
                    <div className="homepage__logo-face homepage__logo-face--right"></div>
                    <div className="homepage__logo-face homepage__logo-face--left"></div>
                    <div className="homepage__logo-face homepage__logo-face--top"></div>
                    <div className="homepage__logo-face homepage__logo-face--bottom"></div>
                  </div>
                </div>
                <h4>Faathum</h4>
              </div>
              <p>Advancing how data is understood</p>
            </div>
            <div className="homepage__footer-links">
              <div className="homepage__footer-column">
                <h5>Product</h5>
                <button onClick={() => onNavigate('demo')}>Features</button>
                <button onClick={() => onNavigate('docs')}>Documentation</button>
              </div>
              <div className="homepage__footer-column">
                <h5>Company</h5>
                <button onClick={() => onNavigate('about')}>About</button>
              </div>
            </div>
          </div>
          <div className="homepage__footer-bottom">
            <p>&copy; 2026 Faathum. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
